import { createResource, createSignal, Show, type JSX } from 'solid-js';
import { A } from '@solidjs/router';
import { authStore } from '@stores/session';
import { isWatched, addWatch, removeWatch } from '@lib/supabase/watchlist';

// "Add to watchlist" toggle — shown on every strain page now that the
// watchlist is a general prescription-planning surface rather than the
// narrow restock-alert signup it was before Phase E. Restock notification
// is a per-entry toggle inside /watchlist itself.

export default function WatchToggle(props: { strainId: string }): JSX.Element {
  // Source must return a stable value across reactive ticks — a fresh
  // object literal would refetch every prop change in any tracking
  // ancestor. Composite string key keeps `===` value-equality intact.
  const [watching, { mutate }] = createResource(
    () => `${authStore.user()?.id ?? ''}|${props.strainId}`,
    async (key) => {
      // Destructure defaults so TS narrows to `string`. Empty `u`
      // routes signed-out callers cleanly to the false branch.
      const [u = '', s = ''] = key.split('|');
      return u ? isWatched(s) : false;
    },
  );
  const [pending, setPending] = createSignal(false);

  async function toggle() {
    if (!authStore.user()) return;
    const next = !(watching() ?? false);
    setPending(true);
    try {
      if (next) await addWatch(props.strainId);
      else await removeWatch(props.strainId);
      // Local mutate instead of refetch — we already know the
      // post-toggle state. Avoids the resource dropping into pending,
      // which would briefly show the "…" disabled label and could
      // re-suspend any tracking-scope read.
      mutate(next);
    } finally {
      setPending(false);
    }
  }

  return (
    <Show
      when={authStore.user()}
      fallback={
        <A
          href="/sign-in"
          class="block text-center rounded-full bg-brand-hex text-brand-green hover:bg-brand-hex-deep py-2 text-sm font-medium transition-colors"
        >
          Sign in to watch
        </A>
      }
    >
      <button
        type="button"
        onClick={toggle}
        disabled={pending() || watching.loading}
        class={`w-full cursor-pointer rounded-full py-2 text-sm font-medium transition-colors ${
          watching()
            ? 'bg-brand-moss/20 text-brand-green border border-brand-moss hover:bg-brand-moss/30'
            : 'bg-brand-green text-brand-cream hover:bg-brand-moss'
        } disabled:opacity-60 disabled:cursor-not-allowed`}
      >
        {watching.loading
          ? '…'
          : watching()
            ? 'In your watchlist \u2013 remove'
            : 'Add to Watchlist'}
      </button>
    </Show>
  );
}
